import { H } from "highlight.run";
import { AppConfig, Environment } from "@/infrastructure/services/config";

export { ErrorBoundary as HighlightErrorBoundary } from "@highlight-run/react";

export type Metadata = {
  [key: string]: string | boolean | number;
};

export type UserIdentity = {
  email: string;
  name: string;
};

export enum TrackEvent {
  LOGIN = "Login",
  LOGOUT = "Logout",
}

const ENV = AppConfig.getInstance().ENV;
const VERSION = AppConfig.getInstance().VERSION;
const HIGHLIGHT_PROJECT_ID = AppConfig.getInstance().HIGHLIGHT_PROJECT_ID;
const HIGHLIGHT_SERVICE_NAME = AppConfig.getInstance().HIGHLIGHT_SERVICE_NAME;

export class HighlightManager {
  private static _isInitialized = false;

  public static initializeHighlight() {
    const isLocalEnvironment = import.meta.env.VITE_ENV?.toLowerCase().includes("local") || false;
    if (isLocalEnvironment || ENV === Environment.DEV || ENV === Environment.ALPHA) return;
    if (this.isInitialized) return;
    H.init(HIGHLIGHT_PROJECT_ID, {
      serviceName: HIGHLIGHT_SERVICE_NAME,
      environment: ENV,
      version: VERSION,
      tracingOrigins: [
        "api.intellirevenue.com",
        "beta.api.intellirevenue.com",
        "alpha.api.intellirevenue.com/api/v1",
        "alpha.api.intellirevenue.com/graphql/v1",
        "127.0.0.1",
        "localhost",
        "localhost:3000/api/v1",
        "localhost:3000/graphql/v1",
      ],
      disableBackgroundRecording: true,
      disableSessionRecording: false,
      consoleMethodsToRecord: ["error"],
      enablePerformanceRecording: true,
      privacySetting: "strict",
      enablePromisePatch: true,
      reportConsoleErrors: true,
      networkRecording: {
        enabled: true,
        recordHeadersAndBody: true,
        networkHeadersToRedact: ["x-brand-authorization", "x-user-authorization"],
        networkBodyKeysToRedact: ["brandAccessToken", "accessToken"],
        destinationDomains: [
          "localhost",
          "127.0.0.1",
          "api.intellirevenue.com/api/v1",
          "api.intellirevenue.com/graphql/v1",
          "beta.api.intellirevenue.com/api/v1",
          "beta.api.intellirevenue.com/graphql/v1",
          "alpha.api.intellirevenue.com/api/v1",
          "alpha.api.intellirevenue.com/graphql/v1",
        ],
      },
    });
    this._isInitialized = true;
  }

  public static setIdentity(identity: UserIdentity) {
    H.identify(identity.email, {
      name: identity.name,
    });
  }

  public static setTrack(event: TrackEvent, properties: Metadata | undefined) {
    H.track(event, properties);
  }

  public static setError(
    message: string,
    error: Error,
    payload?: {
      [key: string]: string;
    }
  ) {
    H.consumeError(error, message, payload);
  }

  public static get isInitialized() {
    return this._isInitialized;
  }
}
